<template>
  <div class="grid-editor-wrap">
    <main class="page">

      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questions" class="breadcrumb-link">Questions</router-link>
        / {{ breadcrumbTail }}
      </p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">{{ pageTitle }}</h1>
          <p class="page-subtitle">Numeric grid: several number fields answered on one card</p>
        </div>
        <div class="page-header-actions">
          <button type="button" class="btn btn-secondary" @click="onCancel">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isValid || saving"
            @click="onSave"
          >{{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create question') }}</button>
        </div>
      </div>

      <p v-if="serverError" class="server-error" role="alert">{{ serverError }}</p>

      <div v-if="loadError" class="load-error">
        <p>{{ loadError }}</p>
        <router-link to="/admin/questions" class="back-link">← Back</router-link>
      </div>

      <div v-else-if="loading" class="loading-state">Loading question…</div>

      <div v-else class="editor-grid">
        <!-- ───────── LEFT: form ───────── -->
        <section class="form-column">

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Identification</legend>

            <div class="field">
              <label class="field-label" :for="`${uid}-code`">Code</label>
              <input
                :id="`${uid}-code`"
                v-model="form.code"
                type="text"
                class="field-input mono"
                :class="{ 'has-error': errors.code && touched.code }"
                placeholder="HH_SPEND"
                autocomplete="off"
                spellcheck="false"
                @blur="touched.code = true"
                @input="onCodeInput"
              >
              <p class="field-hint">
                Short identifier, uppercase letters, digits, and underscores.
                Must be unique within your organization. Each row exports as
                <code>CODE__row_1</code>, <code>CODE__row_2</code>, and so on.
              </p>
              <p v-if="errors.code && touched.code" class="field-error">{{ errors.code }}</p>
            </div>

            <div class="field">
              <label class="field-label">Section</label>
              <button
                type="button"
                class="section-pick-btn"
                :class="{ 'is-unassigned': form.section_id == null }"
                @click="showSectionPicker = true"
              >
                <span class="section-pick-label">{{ sectionPickLabel }}</span>
                <span class="section-pick-action">{{ form.section_id == null ? 'Pick section' : 'Change' }}</span>
              </button>
              <button
                v-if="form.section_id != null"
                type="button"
                class="section-clear-btn"
                @click="form.section_id = null"
              >Clear (Unassigned)</button>
            </div>

            <div class="field">
              <label class="field-label">Status</label>
              <div class="radio-row">
                <label class="radio-option">
                  <input type="radio" v-model="form.status" value="active">
                  <span>Active</span>
                </label>
                <label class="radio-option">
                  <input type="radio" v-model="form.status" value="archived">
                  <span>Archived</span>
                </label>
              </div>
            </div>
          </fieldset>

          <fieldset class="fieldset">
            <legend class="fieldset-legend">Question content</legend>

            <LocaleSelector
              v-model="activeLang"
              :locales="SUPPORTED_LOCALES"
              :labels="LOCALE_LABELS"
              :filled-fn="isLocaleFilled"
              hint="Fill in any or all languages"
              aria-label="Translation language"
            />

            <div class="field">
              <label class="field-label" :for="`${uid}-title-${activeLang}`">
                Question title <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
              </label>
              <textarea
                :id="`${uid}-title-${activeLang}`"
                v-model="form.content.title[activeLang]"
                class="field-input field-textarea"
                :class="{ 'has-error': errors.title && touched.title }"
                rows="2"
                placeholder="Wie viel gibt Ihr Haushalt pro Monat aus für …?"
                @blur="touched.title = true"
              ></textarea>
              <p class="field-hint">
                Asked once, above every row. Required in at least one language.
              </p>
              <p v-if="errors.title && touched.title" class="field-error">{{ errors.title }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-subtitle-${activeLang}`">
                Section label <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-subtitle-${activeLang}`"
                v-model="form.content.subtitle[activeLang]"
                type="text"
                class="field-input"
                placeholder="Haushalt"
              >
              <p class="field-hint">
                Small eyebrow above the title. Only shown when the card has no
                section of its own.
              </p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-prompt-${activeLang}`">
                Helper line <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-prompt-${activeLang}`"
                v-model="form.content.prompt[activeLang]"
                type="text"
                class="field-input"
                placeholder="Nur Zahlen, z. B. 60 statt sechzig."
              >
            </div>

            <div class="field image-field">
              <label class="field-label">
                Stem image <span class="optional">(optional)</span>
              </label>
              <div class="image-block" :class="{ 'is-empty': form.content.image_id == null }">
                <div class="image-thumb-wrap">
                  <img
                    v-if="pickedImageRecord"
                    class="image-thumb"
                    :src="imageUrl(pickedImageRecord)"
                    :alt="altTextFor(pickedImageRecord, activeLang)"
                    @error="onImgError($event, pickedImageRecord, activeLang)"
                  >
                  <div v-else class="image-placeholder"><span aria-hidden="true">🖼</span></div>
                </div>
                <div class="image-actions">
                  <button type="button" class="btn btn-small" @click="showImagePicker = true">
                    {{ form.content.image_id == null ? 'Pick image' : 'Change image' }}
                  </button>
                  <button
                    v-if="form.content.image_id != null"
                    type="button"
                    class="btn btn-small btn-ghost"
                    @click="form.content.image_id = null"
                  >Remove</button>
                  <span v-if="form.content.image_id != null" class="image-id mono">#{{ String(form.content.image_id).padStart(4, '0') }}</span>
                </div>
              </div>
              <p v-if="imageLoadError" class="field-hint dim">{{ imageLoadError }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-help-${activeLang}`">
                Help text <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <textarea
                :id="`${uid}-help-${activeLang}`"
                v-model="form.help_text[activeLang]"
                class="field-input field-textarea"
                rows="2"
                placeholder="Shown when the user taps the info button."
              ></textarea>
              <p class="field-hint">Leave empty in all languages to hide the info button.</p>
            </div>
          </fieldset>

          <!-- ── Rows: the grid itself ── -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Rows</legend>

            <div class="field">
              <label class="checkbox-option">
                <input type="checkbox" v-model="form.content.input.required">
                <span>Required — every row must be filled before advancing</span>
              </label>
              <p class="field-hint">
                All rows or none: requiredness is a property of the card, not of
                an individual row. Leave it off for a grid where "(optional)"
                appears in the question, like height and weight.
                <strong>Not yet enforced at runtime</strong>: the revision
                snapshot does not carry <code>required</code> through, so no
                question of any type currently gates. The setting is stored and
                will take effect when that is plumbed through.
              </p>
            </div>

            <!-- Shared config. Writes the same values into every row so the
                 common case (four CHF amounts) is one edit, while the saved
                 shape stays per-row and the runtime never has to merge. -->
            <div class="shared-config">
              <p class="shared-config-title">Apply to every row</p>
              <div class="config-row3">
                <div class="config-cell">
                  <label class="field-label" :for="`${uid}-shared-min`">Minimum</label>
                  <input :id="`${uid}-shared-min`" v-model="shared.min" type="number" step="any" class="field-input number-input" placeholder="none">
                </div>
                <div class="config-cell">
                  <label class="field-label" :for="`${uid}-shared-max`">Maximum</label>
                  <input :id="`${uid}-shared-max`" v-model="shared.max" type="number" step="any" class="field-input number-input" placeholder="none">
                </div>
                <div class="config-cell">
                  <label class="field-label" :for="`${uid}-shared-decimals`">Decimals</label>
                  <input :id="`${uid}-shared-decimals`" v-model="shared.decimals" type="number" min="0" step="1" class="field-input number-input">
                </div>
              </div>
              <div class="pattern-presets">
                <span class="pattern-presets-label">Unit preset:</span>
                <button
                  v-for="p in UNIT_PRESETS"
                  :key="p.label"
                  type="button"
                  class="preset-chip"
                  :title="`Sets every row to ${p.label} with ${p.decimals} decimals`"
                  @click="applySharedPreset(p)"
                >{{ p.label }}</button>
              </div>
              <button type="button" class="btn btn-small apply-all-btn" @click="applySharedToAllRows">
                Apply min / max / decimals to all {{ form.content.rows.length }} rows
              </button>
              <p class="field-hint">
                A one-shot copy, not a live link: edit any row afterwards and it
                keeps its own values. Use it for a grid where every row is the
                same kind of number (four CHF amounts), then skip it where they
                differ (height in cm, weight in kg).
              </p>
            </div>

            <p v-if="errors.rows_count" class="field-error">{{ errors.rows_count }}</p>

            <div class="row-list">
              <div
                v-for="(row, idx) in form.content.rows"
                :key="row.id"
                class="row-card"
              >
                <div class="row-card-head">
                  <span class="row-id mono">{{ row.id }}</span>
                  <div class="row-card-actions">
                    <button
                      type="button"
                      class="icon-btn"
                      :disabled="idx === 0"
                      title="Move up"
                      @click="moveRow(idx, -1)"
                    >↑</button>
                    <button
                      type="button"
                      class="icon-btn"
                      :disabled="idx === form.content.rows.length - 1"
                      title="Move down"
                      @click="moveRow(idx, 1)"
                    >↓</button>
                    <button
                      type="button"
                      class="icon-btn icon-btn-danger"
                      :disabled="form.content.rows.length <= 2"
                      title="Delete row"
                      @click="deleteRow(idx)"
                    >✕</button>
                  </div>
                </div>

                <div class="field">
                  <label class="field-label" :for="`${uid}-${row.id}-label`">
                    Row label <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                  </label>
                  <input
                    :id="`${uid}-${row.id}-label`"
                    v-model="row.label[activeLang]"
                    type="text"
                    class="field-input"
                    :class="{ 'has-error': errors.row_labels[row.id] }"
                    placeholder="Lebensmittel im Supermarkt"
                  >
                  <p v-if="errors.row_labels[row.id]" class="field-error">{{ errors.row_labels[row.id] }}</p>
                </div>

                <div class="config-row4">
                  <div class="config-cell">
                    <label class="field-label" :for="`${uid}-${row.id}-min`">Min</label>
                    <input :id="`${uid}-${row.id}-min`" v-model="row.input.min" type="number" step="any" class="field-input number-input" placeholder="none">
                  </div>
                  <div class="config-cell">
                    <label class="field-label" :for="`${uid}-${row.id}-max`">Max</label>
                    <input :id="`${uid}-${row.id}-max`" v-model="row.input.max" type="number" step="any" class="field-input number-input" placeholder="none">
                  </div>
                  <div class="config-cell">
                    <label class="field-label" :for="`${uid}-${row.id}-dec`">Decimals</label>
                    <input :id="`${uid}-${row.id}-dec`" v-model="row.input.decimals" type="number" min="0" step="1" class="field-input number-input">
                  </div>
                  <div class="config-cell">
                    <label class="field-label" :for="`${uid}-${row.id}-unit`">
                      Unit <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                    </label>
                    <input :id="`${uid}-${row.id}-unit`" v-model="row.input.unit[activeLang]" type="text" class="field-input" placeholder="CHF">
                  </div>
                </div>
                <p v-if="errors.row_ranges[row.id]" class="field-error">{{ errors.row_ranges[row.id] }}</p>

                <button
                  type="button"
                  class="tooltip-toggle"
                  @click="toggleTooltipEditor(row.id)"
                >
                  {{ tooltipExpanded[row.id] ? '− Hide' : '+ Add' }} row help text
                  <span v-if="rowHasAnyTooltip(row)" class="tooltip-dot" title="Has help text">•</span>
                </button>
                <div v-if="tooltipExpanded[row.id]" class="field">
                  <textarea
                    v-model="row.tooltip_body[activeLang]"
                    class="field-input field-textarea"
                    rows="2"
                    :placeholder="`Help for this row (${LOCALE_LABELS[activeLang]})`"
                  ></textarea>
                  <p class="field-hint">Leave empty in every language to hide this row's info button.</p>
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn btn-small add-row-btn"
              :disabled="form.content.rows.length >= MAX_ROWS"
              @click="addRow"
            >+ Add row</button>
            <p class="field-hint">
              {{ remainingRows }} of {{ MAX_ROWS }} rows remaining. Two is the
              minimum: a one-row grid is a Number question.
            </p>
          </fieldset>

        </section>

        <!-- ───────── RIGHT: live preview (the real runtime card) ───────── -->
        <aside class="preview-column">
          <div class="preview-label">Live preview · {{ LOCALE_LABELS[activeLang] }}</div>
          <div class="preview-frame">
            <div class="preview-stage">
              <div class="preview-card">
                <NumericGridCard
                  :key="previewKey"
                  :item="previewItem"
                  :state="previewState"
                  @state-changed="previewState = $event"
                />
              </div>
            </div>
          </div>
          <p class="preview-note">
            The actual runtime card, rendered from this form in the
            <strong>{{ LOCALE_LABELS[activeLang] }}</strong> language. Type in the
            fields to try the per-row validation; switch tabs to preview another
            language.
          </p>
        </aside>
      </div>
    </main>

    <div v-if="showLeaveConfirm" class="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="leave-title" @click.self="onCancelLeave">
      <div class="modal modal-confirm">
        <h2 id="leave-title" class="modal-title">Discard changes?</h2>
        <p class="modal-text">Your unsaved changes will be lost.</p>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="onCancelLeave">Keep editing</button>
          <button type="button" class="btn btn-danger" @click="onConfirmLeave">Discard</button>
        </div>
      </div>
    </div>

    <SectionPickerPopup
      v-if="showSectionPicker"
      :current-section-id="form.section_id"
      @select-section="onSectionPicked"
      @close="showSectionPicker = false"
    />

    <ImagePicker
      v-if="showImagePicker"
      :image-cache="imageCache"
      :initial-image-id="form.content.image_id"
      @picked="onImagePicked"
      @cancel="showImagePicker = false"
    />

    <transition name="toast-fade">
      <div v-if="toast" class="toast" :class="`toast-${toast.kind}`" role="status" aria-live="polite">{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
// NumericGridEditor — authoring UI for question_type = 'numeric_grid'.
//
// Page shell follows NumberEditor (the row config is a number's config);
// row add/delete/reorder follows MatrixEditor (the other multi-row type).
// Chrome is duplicated rather than extracted, per the project convention:
// the two pages may well diverge.
//
// Row ids are allocated monotonically and never reused, exactly as
// MatrixEditor does with item_N, because a row id is an export column name
// (CODE__row_2) and rebinding one to a different meaning would silently
// corrupt an analysis that spans revisions.

import NumericGridCard from '@/components/quiz/runtime/cards/NumericGridCard.vue';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import SectionPickerPopup from '@/components/admin/SectionPickerPopup.vue';
import ImagePicker from '@/components/image_picker/ImagePicker.vue';
import { imageUrl, altTextFor, onImgError } from '@/services/imageHelpers';
import { loadImageMap } from '@/services/imageApi';
import apiService from '@/services/apiService';
import {
  questionsState,
  blankI18n,
  ensureAllLocales,
  localizeI18n,
  CODE_REGEX,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '../_data/questions.js';

const ROW_ID_RE = /^row_[1-9][0-9]*$/;
// Mirrors MAX_GRID_ROWS in backend/src/routers/seq_questions.py.
const MAX_ROWS = 20;

const UNIT_PRESETS = [
  { label: 'CHF', unit: { de: 'CHF', en: 'CHF', fr: 'CHF', it: 'CHF' }, decimals: 0 },
  { label: 'kg', unit: { de: 'kg', en: 'kg', fr: 'kg', it: 'kg' }, decimals: 1 },
  { label: 'cm', unit: { de: 'cm', en: 'cm', fr: 'cm', it: 'cm' }, decimals: 0 },
  { label: 'Minuten', unit: { de: 'Minuten', en: 'minutes', fr: 'minutes', it: 'minuti' }, decimals: 0 },
  { label: 'mal', unit: { de: 'mal', en: 'times', fr: 'fois', it: 'volte' }, decimals: 0 },
];

let uidCounter = 0;

function trimI18n(i18n) {
  const out = {};
  if (i18n && typeof i18n === 'object') {
    for (const loc of SUPPORTED_LOCALES) {
      const v = i18n[loc];
      if (typeof v === 'string' && v.trim()) out[loc] = v.trim();
    }
  }
  return out;
}

function hasAnyLocaleFilled(i18n) {
  if (!i18n || typeof i18n !== 'object') return false;
  return SUPPORTED_LOCALES.some(loc => typeof i18n[loc] === 'string' && i18n[loc].trim().length > 0);
}

function numOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function decimalsOrZero(v) {
  const n = Number(v);
  return Number.isInteger(n) && n >= 0 ? n : 0;
}

export default {
  name: 'NumericGridEditor',
  components: { NumericGridCard, LocaleSelector, SectionPickerPopup, ImagePicker },
  data() {
    uidCounter += 1;
    return {
      UNIT_PRESETS,
      MAX_ROWS,
      uid: `nge-${uidCounter}`,
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      activeLang: DEFAULT_LOCALE,
      form: this.buildBlankForm(),
      shared: { min: '', max: '', decimals: 0 },
      nextRowInt: 2,
      initialSnapshot: null,
      touched: { code: false, title: false },
      tooltipExpanded: {},
      serverError: null,
      loadError: null,
      loading: false,
      saving: false,
      imageCache: new Map(),
      imageLoadError: null,
      previewState: null,
      showLeaveConfirm: false,
      showSectionPicker: false,
      showImagePicker: false,
      pendingNext: null,
      cancelPending: false,
      toast: null,
      toastTimer: null,
    };
  },
  computed: {
    isEdit() { return this.$route.name === 'QuestionEditorEdit'; },
    questionId() { return this.isEdit ? Number(this.$route.params.id) : null; },
    sections() { return questionsState.sections; },
    sectionPickLabel() {
      if (this.form.section_id == null) return 'Unassigned';
      const s = this.sections.find(x => x.id === this.form.section_id);
      if (!s) return `Section ${this.form.section_id}`;
      return localizeI18n(s.name, 'en') || s.slug || `Section ${s.id}`;
    },
    breadcrumbTail() {
      if (this.isEdit) return this.initialSnapshot ? `Edit · ${this.form.code || 'question'}` : 'Edit';
      return 'New numeric grid question';
    },
    pageTitle() { return this.isEdit ? 'Edit question' : 'New numeric grid question'; },
    pickedImageRecord() {
      const id = this.form.content.image_id;
      if (id == null) return null;
      return this.imageCache.get(id) || null;
    },
    remainingRows() { return Math.max(0, MAX_ROWS - this.form.content.rows.length); },
    errors() {
      const e = { row_labels: {}, row_ranges: {} };
      const code = (this.form.code || '').trim();
      if (!code) e.code = 'Code is required.';
      else if (!CODE_REGEX.test(code)) e.code = 'Code must be uppercase letters, digits or underscores. Max 32 chars.';

      if (!hasAnyLocaleFilled(this.form.content.title)) {
        e.title = 'Question title is required in at least one language.';
      }

      const rows = this.form.content.rows;
      if (rows.length < 2) e.rows_count = 'A numeric grid needs at least 2 rows.';
      else if (rows.length > MAX_ROWS) e.rows_count = `No more than ${MAX_ROWS} rows are allowed.`;

      for (const row of rows) {
        if (!hasAnyLocaleFilled(row.label)) {
          e.row_labels[row.id] = 'Row label is required in at least one language.';
        }
        const min = numOrNull(row.input.min);
        const max = numOrNull(row.input.max);
        if (min != null && max != null && min > max) {
          e.row_ranges[row.id] = 'Minimum must be less than or equal to maximum.';
        }
      }
      return e;
    },
    isValid() {
      const e = this.errors;
      if (e.code || e.title || e.rows_count) return false;
      if (Object.keys(e.row_labels).length > 0) return false;
      if (Object.keys(e.row_ranges).length > 0) return false;
      return true;
    },
    isDirty() {
      if (!this.initialSnapshot) return false;
      return JSON.stringify(this.serializeForm()) !== JSON.stringify(this.initialSnapshot);
    },
    previewItem() {
      const only = (field) => {
        const v = (field && field[this.activeLang]) ? field[this.activeLang].trim() : '';
        return v ? { [this.activeLang]: v } : {};
      };
      let sectionName = null;
      if (this.form.section_id != null) {
        const s = this.sections.find(x => x.id === this.form.section_id);
        if (s) sectionName = only(s.name && typeof s.name === 'object' ? s.name : {});
      }
      return {
        id: 'preview',
        item_type: 'question',
        section_id: this.form.section_id,
        section_name: sectionName,
        section_position: null,
        section_count: null,
        item: {
          question_type: 'numeric_grid',
          required: this.form.content.input.required,
          content: {
            title: only(this.form.content.title),
            subtitle: only(this.form.content.subtitle),
            prompt: only(this.form.content.prompt),
            image_id: this.form.content.image_id,
            input: { required: this.form.content.input.required },
            rows: this.form.content.rows.map(r => ({
              id: r.id,
              label: only(r.label),
              tooltip_body: only(r.tooltip_body),
              input: {
                min: numOrNull(r.input.min),
                max: numOrNull(r.input.max),
                decimals: decimalsOrZero(r.input.decimals),
                unit: only(r.input.unit),
              },
            })),
          },
        },
      };
    },
    previewKey() {
      // Remount the preview when the row STRUCTURE or numeric config changes,
      // so the card's local raw-text state cannot outlive the config it
      // belongs to. Deliberately excludes labels and tooltips: those re-render
      // reactively, and keying on them would remount on every keystroke and
      // throw away whatever the author had typed into the preview to test it.
      const shape = this.form.content.rows.map(r => [
        r.id, r.input.min, r.input.max, r.input.decimals,
        (r.input.unit && r.input.unit[this.activeLang]) || '',
      ].join(':')).join('|');
      return `${this.activeLang}|${this.form.content.input.required}|${shape}`;
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(id) {
        if (this.isEdit) this.loadExisting(id);
        else this.resetForNew();
      },
    },
  },
  created() {
    if (!this.isEdit) this.resetForNew();
  },
  mounted() {
    if (!questionsState.sections.length) this.fetchSections();
    this.loadImages();
  },
  methods: {
    buildBlankForm() {
      return {
        code: '',
        section_id: null,
        status: 'active',
        content: {
          title: blankI18n(),
          subtitle: blankI18n(),
          prompt: blankI18n(),
          image_id: null,
          input: { required: false },
          rows: [this._blankRow('row_1'), this._blankRow('row_2')],
        },
        help_text: blankI18n(),
      };
    },

    _blankRow(id) {
      return {
        id,
        label: blankI18n(),
        tooltip_body: blankI18n(),
        input: { min: '', max: '', decimals: 0, unit: blankI18n() },
      };
    },

    async fetchSections() {
      try {
        const sections = await apiService.fetchSections();
        questionsState.sections.splice(0, questionsState.sections.length, ...sections);
      } catch (e) {
        console.warn('Could not load sections:', e);
      }
    },

    async loadImages() {
      // forceRefresh: pick up images uploaded earlier this session, which the
      // session-memoized map would otherwise miss.
      try {
        this.imageCache = await loadImageMap(true);
      } catch (e) {
        this.imageLoadError = 'Image library could not be loaded; picking is unavailable.';
      }
    },

    isLocaleFilled(loc) {
      const c = this.form.content;
      const fields = [
        c.title && c.title[loc],
        c.subtitle && c.subtitle[loc],
        c.prompt && c.prompt[loc],
        this.form.help_text && this.form.help_text[loc],
      ];
      for (const row of c.rows) {
        fields.push(row.label && row.label[loc]);
        fields.push(row.tooltip_body && row.tooltip_body[loc]);
        fields.push(row.input.unit && row.input.unit[loc]);
      }
      return fields.some(v => typeof v === 'string' && v.trim().length > 0);
    },

    resetForNew() {
      this.form = this.buildBlankForm();
      this.shared = { min: '', max: '', decimals: 0 };
      this.nextRowInt = 2;
      this.tooltipExpanded = {};
      this.initialSnapshot = this.serializeForm();
      this.loadError = null;
      this.serverError = null;
      this.previewState = null;
      this.touched = { code: false, title: false };
    },

    async loadExisting(id) {
      this.loadError = null;
      this.serverError = null;
      this.loading = true;
      try {
        const q = await apiService.fetchQuestion(id);
        if (q.question_type !== 'numeric_grid') {
          this.loadError = `Question ${id} is a ${q.question_type}, not a numeric grid question.`;
          this.initialSnapshot = null;
          return;
        }
        const content = q.content || {};
        const rows = (Array.isArray(content.rows) ? content.rows : []).map((r, idx) => {
          const input = r.input || {};
          return {
            id: typeof r.id === 'string' && ROW_ID_RE.test(r.id) ? r.id : `row_${idx + 1}`,
            label: ensureAllLocales(r.label),
            tooltip_body: ensureAllLocales(r.tooltip_body),
            input: {
              min: input.min == null ? '' : String(input.min),
              max: input.max == null ? '' : String(input.max),
              decimals: Number.isInteger(input.decimals) ? input.decimals : 0,
              unit: ensureAllLocales(input.unit),
            },
          };
        });
        // A stored question with fewer than two rows shouldn't exist (the API
        // rejects it), but the editor must still open rather than break, so
        // pad up to the minimum instead of rendering an unusable form.
        while (rows.length < 2) rows.push(this._blankRow(`row_${rows.length + 1}`));

        this.form = {
          code: q.code,
          section_id: q.section_id,
          status: q.status,
          content: {
            title: ensureAllLocales(content.title),
            subtitle: ensureAllLocales(content.subtitle),
            prompt: ensureAllLocales(content.prompt),
            image_id: content.image_id ?? null,
            input: { required: (content.input || {}).required === true },
            rows,
          },
          help_text: ensureAllLocales(q.help_text),
        };
        // Highest row_N ever assigned, so "+ Add row" never reissues an id
        // that pointed at a deleted row: those ids are export column names.
        this.nextRowInt = rows.reduce((m, row) => {
          if (!ROW_ID_RE.test(row.id || '')) return m;
          const n = parseInt(row.id.slice(4), 10);
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, 0);
        this.tooltipExpanded = {};
        this.initialSnapshot = this.serializeForm();
        this.touched = { code: false, title: false };
      } catch (e) {
        this.initialSnapshot = null;
        if (e?.response?.status === 404) this.loadError = `No question found with id ${id}.`;
        else {
          const detail = e?.response?.data?.detail;
          this.loadError = typeof detail === 'string' ? detail : 'Failed to load question from the server.';
        }
      } finally {
        this.loading = false;
      }
    },

    serializeForm() {
      const f = this.form;
      return {
        code: (f.code || '').trim(),
        section_id: f.section_id ?? null,
        status: f.status,
        content: {
          title: trimI18n(f.content.title),
          subtitle: trimI18n(f.content.subtitle),
          prompt: trimI18n(f.content.prompt),
          image_id: f.content.image_id ?? null,
          input: { required: !!f.content.input.required },
          rows: f.content.rows.map(r => ({
            id: r.id,
            label: trimI18n(r.label),
            tooltip_body: trimI18n(r.tooltip_body),
            input: {
              min: numOrNull(r.input.min),
              max: numOrNull(r.input.max),
              decimals: decimalsOrZero(r.input.decimals),
              unit: trimI18n(r.input.unit),
            },
          })),
        },
        help_text: trimI18n(f.help_text),
      };
    },

    onCodeInput(e) { this.form.code = (e.target.value || '').toUpperCase(); },
    onImagePicked(id) { this.form.content.image_id = id; this.showImagePicker = false; },

    addRow() {
      if (this.form.content.rows.length >= MAX_ROWS) return;
      this.nextRowInt += 1;
      const row = this._blankRow(`row_${this.nextRowInt}`);
      // A new row inherits the shared config, which is almost always what the
      // author wants: they are adding a fifth CHF amount, not a new kind of
      // number. They can still override it on the row.
      row.input.min = this.shared.min;
      row.input.max = this.shared.max;
      row.input.decimals = this.shared.decimals;
      this.form.content.rows.push(row);
    },

    deleteRow(idx) {
      if (this.form.content.rows.length <= 2) return;
      const removed = this.form.content.rows[idx];
      this.form.content.rows.splice(idx, 1);
      if (removed && this.tooltipExpanded[removed.id]) {
        const next = { ...this.tooltipExpanded };
        delete next[removed.id];
        this.tooltipExpanded = next;
      }
    },

    moveRow(idx, delta) {
      const rows = this.form.content.rows;
      const target = idx + delta;
      if (target < 0 || target >= rows.length) return;
      const [row] = rows.splice(idx, 1);
      rows.splice(target, 0, row);
    },

    toggleTooltipEditor(rowId) {
      this.tooltipExpanded = { ...this.tooltipExpanded, [rowId]: !this.tooltipExpanded[rowId] };
    },

    rowHasAnyTooltip(row) { return hasAnyLocaleFilled(row.tooltip_body); },

    applySharedPreset(preset) {
      this.shared.decimals = preset.decimals;
      for (const row of this.form.content.rows) {
        row.input.unit = { ...preset.unit };
        row.input.decimals = preset.decimals;
      }
    },

    applySharedToAllRows() {
      for (const row of this.form.content.rows) {
        row.input.min = this.shared.min;
        row.input.max = this.shared.max;
        row.input.decimals = this.shared.decimals;
      }
    },

    onSectionPicked(section) {
      this.form.section_id = section.id;
      const idx = questionsState.sections.findIndex(s => s.id === section.id);
      if (idx === -1) questionsState.sections.push(section);
      else questionsState.sections.splice(idx, 1, section);
      this.showSectionPicker = false;
    },

    async onSave() {
      this.touched.code = true;
      this.touched.title = true;
      if (!this.isValid || this.saving) return;

      this.serverError = null;
      this.saving = true;
      const f = this.serializeForm();
      const payload = {
        code: f.code,
        section_id: f.section_id,
        question_type: 'numeric_grid',
        content: f.content,
        help_text: f.help_text,
        status: f.status,
      };

      try {
        const saved = this.isEdit
          ? await apiService.updateQuestion(this.questionId, {
            code: payload.code,
            section_id: payload.section_id,
            content: payload.content,
            help_text: payload.help_text,
            status: payload.status,
          })
          : await apiService.createQuestion(payload);
        this.initialSnapshot = this.serializeForm();
        this.showToast('success', this.isEdit ? 'Question saved.' : `Created question ${saved.code}.`);
        this.$router.push('/admin/questions');
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          this.serverError = typeof detail === 'string' ? detail : 'A question with that code already exists.';
        } else if (status === 422) {
          this.serverError = Array.isArray(detail) ? detail.map(d => d.msg).join(' ') : (typeof detail === 'string' ? detail : 'Validation failed.');
        } else {
          this.serverError = typeof detail === 'string' ? detail : 'Could not save question. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    onCancel() {
      if (this.isDirty) { this.cancelPending = true; this.showLeaveConfirm = true; return; }
      this.$router.push('/admin/questions');
    },
    onCancelLeave() {
      this.showLeaveConfirm = false;
      if (this.pendingNext) { this.pendingNext(false); this.pendingNext = null; }
      this.cancelPending = false;
    },
    onConfirmLeave() {
      this.showLeaveConfirm = false;
      if (this.cancelPending) {
        this.cancelPending = false;
        this.initialSnapshot = this.serializeForm();
        this.$router.push('/admin/questions');
        return;
      }
      if (this.pendingNext) {
        const next = this.pendingNext;
        this.pendingNext = null;
        this.initialSnapshot = this.serializeForm();
        next();
      }
    },
    showToast(kind, text) {
      if (this.toastTimer) { clearTimeout(this.toastTimer); this.toastTimer = null; }
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 3500;
      this.toastTimer = setTimeout(() => { this.toast = null; this.toastTimer = null; }, ms);
    },

    imageUrl,
    altTextFor,
    onImgError,
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.cancelPending) {
      this.pendingNext = next;
      this.showLeaveConfirm = true;
    } else {
      next();
    }
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
};
</script>

<style scoped>
.grid-editor-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --color-warn-dark: #6B4A0F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.grid-editor-wrap *,
.grid-editor-wrap *::before,
.grid-editor-wrap *::after { box-sizing: border-box; }

.page { max-width: 1080px; margin: 0 auto; padding: 2.5rem 1.5rem 4rem; }

.breadcrumb { font-size: 13px; color: var(--color-text-tertiary); margin-bottom: 1rem; letter-spacing: 0.02em; }
.breadcrumb-link { color: var(--color-text-secondary); text-decoration: none; border-bottom: 1px solid transparent; }
.breadcrumb-link:hover { border-bottom-color: var(--color-text-secondary); }

.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 1.25rem; flex-wrap: wrap; }
.page-header-text { flex: 1; min-width: 240px; }
.page-header-actions { display: flex; gap: 8px; flex-shrink: 0; margin-top: 4px; }
.page-title { font-size: 32px; font-weight: 600; margin: 0 0 0.5rem; line-height: 1.2; }
.page-subtitle { font-size: 16px; color: var(--color-text-secondary); margin: 0; font-weight: 400; }

.btn { padding: 9px 16px; border-radius: var(--radius-md); font-size: 13px; font-family: inherit; font-weight: 500; cursor: pointer; border: 0.5px solid var(--color-border); transition: opacity 0.15s, background 0.15s, border-color 0.15s, color 0.15s; }
.btn:disabled { cursor: not-allowed; opacity: 0.45; }
.btn-secondary { background: var(--color-bg-card); color: var(--color-text-primary); }
.btn-secondary:hover:not(:disabled) { background: var(--color-bg-soft); border-color: var(--color-border-strong); }
.btn-primary { background: var(--color-accent); color: white; border-color: var(--color-accent); }
.btn-primary:hover:not(:disabled) { background: var(--color-accent-dark); border-color: var(--color-accent-dark); }
.btn-danger { background: var(--color-bad); color: white; border-color: var(--color-bad); }
.btn-danger:hover { background: var(--color-bad-dark); border-color: var(--color-bad-dark); }
.btn-small { padding: 6px 12px; font-size: 12px; }
.btn-ghost { background: none; border-color: transparent; color: var(--color-text-tertiary); }
.btn-ghost:hover { color: var(--color-bad); }

.server-error { font-size: 13px; color: #6B1F18; background: var(--color-bad-soft); border: 0.5px solid rgba(200, 57, 46, 0.45); border-radius: var(--radius-md); padding: 10px 14px; margin: 0 0 1.25rem; line-height: 1.5; }
.load-error { text-align: center; padding: 3rem 1rem; color: var(--color-text-secondary); }
.loading-state { text-align: center; padding: 3rem 1rem; color: var(--color-text-tertiary); font-size: 14px; }
.back-link { color: var(--color-accent); text-decoration: none; font-size: 14px; }
.back-link:hover { text-decoration: underline; }

.editor-grid { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 32px; align-items: start; }
@media (max-width: 900px) { .editor-grid { grid-template-columns: 1fr; } }

.form-column { display: flex; flex-direction: column; gap: 1.5rem; min-width: 0; }
.preview-column { position: sticky; top: 1.5rem; }
.preview-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--color-text-tertiary); font-weight: 600; margin-bottom: 8px; }
.preview-frame { border: 0.5px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04); }
.preview-stage { background: #f5f4ef; padding: 1.5rem; }
.preview-card { background: #fff; border-radius: 12px; padding: 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
.preview-note { margin: 10px 0 0; font-size: 11.5px; color: var(--color-text-tertiary); font-style: italic; line-height: 1.4; }

.fieldset { border: 0.5px solid var(--color-border); border-radius: var(--radius-lg); padding: 1.25rem 1.5rem 1.5rem; margin: 0; background: var(--color-bg-card); display: flex; flex-direction: column; gap: 1.25rem; }
.fieldset-legend { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: var(--color-text-tertiary); padding: 0 6px; margin-left: -6px; }

.field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 13px; font-weight: 500; color: var(--color-text-secondary); display: inline-flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
.lang-tag { font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; color: var(--color-accent); background: var(--color-accent-soft); padding: 1px 6px; border-radius: 3px; }
.optional { color: var(--color-text-tertiary); font-weight: 400; font-size: 12px; }

.field-input { width: 100%; padding: 10px 12px; border: 0.5px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-card); color: var(--color-text-primary); font-family: var(--font-sans); font-size: 14px; line-height: 1.4; }
.field-input.mono { font-family: var(--font-mono); font-size: 13px; letter-spacing: 0.02em; }
.field-input:focus { outline: none; border-color: var(--color-accent); box-shadow: 0 0 0 2px var(--color-accent-soft); }
.field-input.has-error { border-color: var(--color-bad); box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15); }
.field-textarea { resize: vertical; min-height: 56px; font-family: var(--font-sans); }
.number-input { font-family: var(--font-mono); font-size: 14px; text-align: center; }

.config-row3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.config-row4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.config-cell { display: flex; flex-direction: column; gap: 6px; }
@media (max-width: 640px) {
  .config-row3 { grid-template-columns: 1fr; }
  .config-row4 { grid-template-columns: repeat(2, 1fr); }
}

.checkbox-option { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; color: var(--color-text-primary); cursor: pointer; }
.checkbox-option input { accent-color: var(--color-accent); cursor: pointer; }

.shared-config { border: 0.5px dashed var(--color-border-strong); border-radius: var(--radius-md); padding: 1rem 1.1rem; background: var(--color-bg-soft); display: flex; flex-direction: column; gap: 10px; }
.shared-config-title { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--color-text-tertiary); margin: 0; }
.apply-all-btn { align-self: flex-start; }

.pattern-presets { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.pattern-presets-label { font-size: 12px; color: var(--color-text-tertiary); }
.preset-chip { font-size: 12px; padding: 5px 11px; border-radius: 999px; cursor: pointer; border: 0.5px solid var(--color-border); background: var(--color-bg-card); color: var(--color-text-secondary); font-family: inherit; transition: background 0.15s, border-color 0.15s, color 0.15s; }
.preset-chip:hover { border-color: var(--color-accent); color: var(--color-accent); background: var(--color-accent-soft); }

.row-list { display: flex; flex-direction: column; gap: 12px; }
.row-card { border: 0.5px solid var(--color-border); border-radius: var(--radius-md); padding: 0.9rem 1rem 1rem; background: var(--color-bg-soft); display: flex; flex-direction: column; gap: 10px; }
.row-card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.row-id { font-size: 11px; color: var(--color-text-tertiary); letter-spacing: 0.03em; }
.row-card-actions { display: flex; gap: 4px; }
.icon-btn { width: 26px; height: 26px; display: inline-flex; align-items: center; justify-content: center; border: 0.5px solid var(--color-border); border-radius: 6px; background: var(--color-bg-card); color: var(--color-text-secondary); font-size: 12px; cursor: pointer; font-family: inherit; }
.icon-btn:hover:not(:disabled) { border-color: var(--color-border-strong); color: var(--color-text-primary); }
.icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.icon-btn-danger:hover:not(:disabled) { border-color: var(--color-bad); color: var(--color-bad); }

.tooltip-toggle { align-self: flex-start; background: none; border: none; padding: 2px 0; font-family: inherit; font-size: 12px; color: var(--color-accent); cursor: pointer; }
.tooltip-toggle:hover { text-decoration: underline; }
.tooltip-dot { color: var(--color-accent); font-size: 16px; line-height: 1; }

.add-row-btn { align-self: flex-start; }

.image-field .image-block { display: flex; align-items: center; gap: 14px; }
.image-thumb-wrap { width: 72px; height: 72px; flex-shrink: 0; border-radius: var(--radius-md); border: 0.5px solid var(--color-border); overflow: hidden; background: var(--color-bg-soft); display: flex; align-items: center; justify-content: center; }
.image-thumb { width: 100%; height: 100%; object-fit: cover; }
.image-placeholder { font-size: 26px; color: var(--color-text-tertiary); }
.image-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.image-id { font-size: 12px; color: var(--color-text-tertiary); }

.section-pick-btn { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 9px 12px; border: 0.5px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-card); font-family: inherit; font-size: 14px; color: var(--color-text-primary); cursor: pointer; transition: background 0.15s, border-color 0.15s; }
.section-pick-btn:hover { background: var(--color-bg-soft); border-color: var(--color-border-strong); }
.section-pick-btn.is-unassigned .section-pick-label { color: var(--color-text-tertiary); font-style: italic; }
.section-pick-action { font-size: 12px; font-weight: 600; color: var(--color-accent); letter-spacing: 0.02em; flex-shrink: 0; }
.section-clear-btn { align-self: flex-start; margin-top: 4px; background: none; border: none; color: var(--color-text-tertiary); font-family: inherit; font-size: 12px; cursor: pointer; padding: 2px 0; text-decoration: underline; }
.section-clear-btn:hover { color: var(--color-text-secondary); }

.field-hint { font-size: 11.5px; color: var(--color-text-tertiary); margin: 0; line-height: 1.4; }
.field-hint.dim { font-style: italic; color: var(--color-warn-dark); }
.field-hint code { font-family: var(--font-mono); font-size: 0.92em; background: rgba(0,0,0,0.05); padding: 1px 4px; border-radius: 3px; }
.field-error { font-size: 12px; color: var(--color-bad); margin: 0; }

.radio-row { display: flex; gap: 18px; padding: 4px 0; }
.radio-option { display: inline-flex; align-items: center; gap: 6px; font-size: 14px; color: var(--color-text-primary); cursor: pointer; }
.radio-option input { accent-color: var(--color-accent); cursor: pointer; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.4); display: flex; align-items: center; justify-content: center; padding: 1.25rem; z-index: 2000; }
.modal { background: var(--color-bg-card); border-radius: var(--radius-lg); border: 0.5px solid var(--color-border); padding: 1.5rem; max-width: 420px; width: 100%; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15); }
.modal-title { font-size: 18px; font-weight: 600; margin: 0 0 8px; }
.modal-text { font-size: 14px; color: var(--color-text-secondary); margin: 0 0 1.25rem; line-height: 1.5; }
.modal-footer { display: flex; justify-content: space-between; gap: 10px; }

.toast { position: fixed; left: 50%; bottom: 28px; transform: translateX(-50%); background: var(--color-text-primary); color: white; padding: 11px 18px; border-radius: var(--radius-md); font-size: 13px; line-height: 1.4; box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18); max-width: min(90vw, 520px); z-index: 3000; }
.toast-success { background: #2E7D32; }
.toast-error { background: var(--color-bad-dark); }
.toast-fade-enter-active, .toast-fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.toast-fade-enter-from, .toast-fade-leave-to { opacity: 0; transform: translate(-50%, 8px); }
</style>
